import { action, makeObservable, observable } from 'mobx';
import axios from 'axios';

import Computation from './Computation';
import { MetadataField } from '../metadataFields';
import IHttpException from '../errors/IHttpException';

export type SpendDropdownField = {
  name: string;
  label: string;
  type: 'number' | 'attribute';
};

export default class ComputationsPageModel {
  computations: Computation[];

  computationsInProgress: boolean;

  loading: boolean;

  loadingError: string;

  saving: boolean;

  savingError: IHttpException;

  formError: IHttpException;

  activeComputation: Computation;

  selectedPosition: number;

  modalConfirmRemove: boolean;

  modalConfirmSave: {
    active: boolean;
    withRecompute: boolean;
    selectedOnly: boolean;
  };

  selectedForDeploy: string[];

  selectiveDeployEnabled: boolean;

  modalConfirmCancelChanges: boolean;

  message: string;

  metadataFields: MetadataField[];

  spendFields: SpendDropdownField[];

  isSaving: boolean;

  constructor(other?: ComputationsPageModel) {
    if (other) {
      this.computations = other.computations;
      this.computationsInProgress = other.computationsInProgress;
      this.loading = other.loading;
      this.loadingError = other.loadingError;
      this.saving = other.saving;
      this.savingError = other.savingError;
      this.formError = other.formError;

      this.activeComputation = other.activeComputation;
      this.selectedPosition = null;
      this.modalConfirmRemove = false;
      this.modalConfirmSave = {
        active: false,
        withRecompute: false,
        selectedOnly: false,
      };
      this.selectedForDeploy = [];
      this.selectiveDeployEnabled = other.selectiveDeployEnabled ?? false;
      this.modalConfirmCancelChanges = false;
      this.message = null;
      this.metadataFields = other.metadataFields;
      this.spendFields = other.spendFields ?? [];
      this.isSaving = false;
    } else {
      this.computations = [];
      this.computationsInProgress = false;
      this.loading = false;
      this.loadingError = null;
      this.saving = false;
      this.savingError = null;
      this.formError = null;

      this.activeComputation = null;
      this.selectedPosition = null;
      this.modalConfirmRemove = false;
      this.modalConfirmSave = {
        active: false,
        withRecompute: false,
        selectedOnly: false,
      };
      this.selectedForDeploy = [];
      this.selectiveDeployEnabled = false;
      this.modalConfirmCancelChanges = false;
      this.message = null;
      this.metadataFields = [];
      this.spendFields = [];
      this.isSaving = false;
    }
    makeObservable(this, {
      computations: observable,
      computationsInProgress: observable,
      loading: observable,
      loadingError: observable,
      saving: observable,
      savingError: observable,
      formError: observable,
      activeComputation: observable,
      selectedPosition: observable,
      modalConfirmRemove: observable,
      modalConfirmSave: observable,
      selectedForDeploy: observable,
      selectiveDeployEnabled: observable,
      modalConfirmCancelChanges: observable,
      message: observable,
      metadataFields: observable,
      spendFields: observable,
      isSaving: observable,
      saveComputationsCache: action,
      validateBeforeSave: action,
    });
  }

  async saveComputationsCache(
    tenant: number,
    deletedComputationName?: string
  ): Promise<boolean> {
    this.savingError = null;
    this.isSaving = true;
    // @todo Delete this code in the future as it is a hack against possible issues with the cache
    // Ref: https://github.com/HG Insights/springbok/pull/1375
    this.computations.forEach((computation) => {
      if (['custom', 'available_in_realtime'].includes(computation.context)) {
        // eslint-disable-next-line no-param-reassign
        computation.context = 'configured';
      }
    });

    // Validate BEFORE saving and BLOCK on a genuine validation failure
    // (RGI-1756). Fails OPEN on anything that isn't a 400 — a 403 for
    // non-SuperKudu users, a 5xx, or a network error — so validation problems
    // never prevent a save.
    // On a delete, scope validation to a referential check on the removed
    // computation (deletedComputationName) instead of the active field.
    const passedValidation = await this.validateBeforeSave(
      tenant,
      this.computations,
      deletedComputationName ? undefined : this.activeComputation?.name,
      deletedComputationName
    );
    if (!passedValidation) {
      this.isSaving = false;
      return false; // savingError set by validateBeforeSave → the form shows it
    }

    try {
      await axios.post(
        `/api/tenant/${tenant}/computations/save_cache`,
        this.computations
      );
      this.isSaving = false;
      this.savingError = null;
      this.computationsInProgress = true;
      return true;
    } catch (err) {
      this.isSaving = false;
      this.savingError = err?.response?.data ?? {
        message: 'Unknown error.',
        type: 'other',
        status: 500,
      };
      return false;
    }
  }

  /**
   * Pre-save validation gate (RGI-1756). Returns true if the save may proceed.
   * On a genuine validation failure (HTTP 400) it sets `savingError` — so the
   * form shows the per-rule message — and returns false so the caller blocks the
   * save. FAILS OPEN on anything else (a 403 for non-SuperKudu, a 5xx, or a
   * network error) so validation problems never block a save.
   */
  async validateBeforeSave(
    tenant: number,
    computations: Computation[],
    targetComputationName?: string,
    deletedComputationName?: string
  ): Promise<boolean> {
    try {
      await axios.post(
        `/api/tenant/${tenant}/computations/validate_redshift`,
        computations,
        {
          params: {
            ...(targetComputationName ? { targetComputationName } : {}),
            ...(deletedComputationName ? { deletedComputationName } : {}),
          },
        }
      );
      return true;
    } catch (err) {
      if (err?.response?.status === 400) {
        this.savingError = err.response.data;
        return false;
      }
      return true;
    }
  }
}
